import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress, Divider, Button, Chip, alpha, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublicIcon from "@mui/icons-material/Public";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LogSummary from "./LogSummary";
import ScoreHeader from "./ScoreHeader";
import ParseProgress from "./ParseProgress";
import LogFindings from "./LogFindings";
import LogParamAudit from "./LogParamAudit";
import AnalysisTabs from "./analysis/AnalysisTabs";
import FilterPanel from "./analysis/FilterPanel";
import OscillationPanel from "./analysis/OscillationPanel";
import PIDPanel from "./analysis/PIDPanel";
import CompassPanel from "./analysis/CompassPanel";
import HardwarePanel from "./analysis/HardwarePanel";
import { PanelUnavailable } from "./analysis/PanelShell";
import ErrorBoundary from "../../../ErrorBoundary";
import LogTimeline from "./LogTimeline";
import LogCharts from "./LogCharts";
import { ACCENT } from "./accent";
import {
  getPublicInsight,
  getPublicLog,
  getPublicMessages,
  pollPublicLogUntilDone,
} from "./api/publicTelemetry";
import { getLog, getMessages, pollLogUntilDone } from "./api/newTelemetry";
import { useAuthGate } from "../../../hooks/useAuthGate";
import { joinAsDeveloper } from "../../PublicHeader";
import HardwareId from "./HardwareId";
import { PUBLIC_BASE } from "./paths";
import { Link as RouterLink } from "react-router-dom";

/** Read-only view of a public flight log (route /log-analyzer/public/:id). */
/**
 * Same analysis tabs as the owner's view, minus the two that are not a
 * visitor's to see: the inspection record belongs to whoever owns the aircraft.
 */
const PUBLIC_TABS = [
  { id: "overview", label: "Overview" },
  { id: "findings", label: "Findings" },
  { id: "filters", label: "Filters", module: "filters" },
  { id: "oscillation", label: "Oscillation", module: "oscillation" },
  { id: "pid", label: "PID", module: "pid" },
  { id: "compass", label: "Compass", module: "compass" },
  { id: "hardware", label: "Hardware", module: "hardware" },
  { id: "params", label: "Parameters" },
  { id: "charts", label: "Charts" },
];

/** Both ways in — an existing account, or a new one. */
const AuthButtons = ({ onSignin, center = false }) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: center ? "center" : "flex-start" }}>
    <Button
      size="small"
      variant="contained"
      onClick={onSignin}
      sx={{
        backgroundColor: ACCENT,
        textTransform: "none",
        fontWeight: 600,
        "&:hover": { backgroundColor: ACCENT, filter: "brightness(0.95)" },
      }}
    >
      Sign in
    </Button>
    <Button
      size="small"
      variant="outlined"
      onClick={joinAsDeveloper}
      sx={{ color: ACCENT, borderColor: alpha(ACCENT, 0.5), textTransform: "none", fontWeight: 600 }}
    >
      Join as developer
    </Button>
  </Box>
);

/** The one sign-in prompt this page has, wherever it lands. */
const SignInCard = ({ theme, title, body, onSignin }) => (
  <Box
    sx={{
      p: 3,
      borderRadius: 2,
      border: `1px solid ${alpha(ACCENT, 0.35)}`,
      backgroundColor: alpha(ACCENT, 0.06),
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <LockOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
      {body}
    </Typography>
    <AuthButtons onSignin={onSignin} />
  </Box>
);

/**
 * The rank, behind frosted glass.
 *
 * The number is real and already computed — blurring it is the point: a visitor
 * can see their flight was scored without being told what it scored. Hidden
 * from the accessibility tree, since the readable version is the overlay.
 */
const BlurredRank = ({ log, theme, onSignin }) => (
  <Box sx={{ position: "relative", mb: 3 }}>
    {/* Grey as well as blurred: the ring's colour band is itself the answer. */}
    <Box sx={{ filter: "blur(8px) grayscale(1)", pointerEvents: "none", userSelect: "none" }} aria-hidden>
      <ScoreHeader log={log} />
    </Box>
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        px: 2,
        textAlign: "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LockOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
          This flight has been ranked
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        Sign in to see the number, the grade and the deep analysis behind it.
      </Typography>
      <AuthButtons onSignin={onSignin} center />
    </Box>
  </Box>
);

/** Deep-analysis tabs, by tab id — all behind the sign-in gate for visitors. */
const DEEP_PANELS = {
  filters: FilterPanel,
  oscillation: OscillationPanel,
  pid: PIDPanel,
  compass: CompassPanel,
  hardware: HardwarePanel,
};

const PublicAnalysisPanel = ({ log, module, render: Panel }) => {
  if (!log.analysis) {
    return (
      <PanelUnavailable reason="This log was parsed before deep analysis was available, so these tabs are empty for it. Only the account that owns the log can re-analyse it." />
    );
  }
  const result = log.analysis.modules?.[module];
  if (!result) return <PanelUnavailable reason="This analysis module did not run for this log." />;
  // Keyed by module so switching tabs remounts the boundary fresh rather than
  // carrying a previous panel's error into the next one.
  return (
    <ErrorBoundary
      key={module}
      fallback={<PanelUnavailable reason="This panel hit an unexpected error and couldn't render." />}
    >
      <Panel module={result} />
    </ErrorBoundary>
  );
};

const PublicLogDetail = () => {
  const theme = useTheme();
  const { id } = useParams();
  const { isAuthenticated, requireSignin } = useAuthGate();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insight, setInsight] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [owned, setOwned] = useState(false); // read via the authed route instead
  const [locked, setLocked] = useState(false);
  const [paramAudit, setParamAudit] = useState(null); // fed to the RCA export
  const [tab, setTab] = useState(() => {
    const fromHash = window.location.hash.replace("#", "");
    return PUBLIC_TABS.some((t) => t.id === fromHash) ? fromHash : "overview";
  });

  const selectTab = (next) => {
    setTab(next);
    // `<base href="/">` in index.html is what replaceState resolves a relative
    // URL against, so a bare "#tab" would drop the path and rewrite the address
    // bar to "/#tab" — which reloads onto the landing page.
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${next}`);
  };

  // Per-log document title; restores the gallery title (set by PublicShell) on
  // unmount / when the log changes.
  useEffect(() => {
    if (!log?.file_name) return undefined;
    const prev = document.title;
    document.title = `${log.file_name} — Vecros`;
    return () => {
      document.title = prev;
    };
  }, [log?.file_name]);

  // The one free AI pass, once there's something to analyze. What comes back is
  // the backend's call — anonymous callers get a preview and `locked: true`;
  // nothing here decides the cutoff, it only renders it. A failure is silent:
  // the log itself still reads fine without it.
  useEffect(() => {
    if (log?.status !== "ready") return;
    getPublicInsight(id).then(setInsight).catch(() => setInsight(null));
  }, [id, log?.status]);

  // Arriving straight from an upload means the parse is still running, so keep
  // polling until it lands rather than rendering an empty log.
  // The public route serves only logs the registry lists, so a private id 404s
  // there. That is not a dead end: whoever owns the log still reads it on the
  // authed route, and everyone else gets the sign-in gate instead of an error.
  useEffect(() => {
    const load = async () => {
      try {
        return { data: await getPublicLog(id), isOwned: false };
      } catch (e) {
        if (!isAuthenticated) throw Object.assign(e, { locked: true });
        return { data: await getLog(id), isOwned: true };
      }
    };
    setLoading(true);
    setLocked(false);
    load()
      .then(({ data, isOwned }) => {
        setOwned(isOwned);
        setLog(data);
        setError(null);
        if (data.status !== "pending" && data.status !== "parsing") return;
        setAnalyzing(true);
        (isOwned ? pollLogUntilDone(id) : pollPublicLogUntilDone(id))
          .then(setLog)
          .catch((e) => setError(e.message))
          .finally(() => setAnalyzing(false));
      })
      .catch((e) => {
        setLocked(!!e.locked);
        setError(e.message);
      })
      .finally(() => setLoading(false));
  }, [id, isAuthenticated]);

  const fetchMessages = owned ? getMessages : getPublicMessages;
  // Visitors get the overview and findings; everything the deep analysis paid
  // for stays behind sign-in. Reading via the authed route means they own it.
  const gated = !isAuthenticated && !owned;

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={PUBLIC_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Gallery
      </Button>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : locked ? (
        <SignInCard
          theme={theme}
          title="This log isn't in the public gallery"
          body="Either it was never made public, or it belongs to an account. Sign in to open it — the owner sees the full analysis."
          onSignin={() => requireSignin()}
        />
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : !log ? (
        <Typography color="error" variant="body2">
          Could not load this log.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {log.file_name}
            </Typography>
            <Chip
              icon={<PublicIcon sx={{ fontSize: 16 }} />}
              label="Public"
              size="small"
              sx={{ backgroundColor: `${ACCENT}14`, color: ACCENT, fontWeight: 600 }}
            />
          </Box>

          {(log.tags || []).length > 0 && (
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 3 }}>
              {log.tags.map((t) => (
                <Chip key={t} label={t} size="small" variant="outlined" />
              ))}
            </Box>
          )}

          {log.demo && (
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
              }}
            >
              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                <b>Demo analysis.</b> Log parsing isn't live yet, so these numbers are sample data — not your file.
                The flow below is the real thing: this is what you'll see once the backend ships.
              </Typography>
            </Box>
          )}

          {insight?.preview && (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha(ACCENT, 0.35)}`,
                backgroundColor: alpha(ACCENT, 0.06),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                  What we found
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, whiteSpace: "pre-wrap" }}>
                {insight.preview}
              </Typography>

              {insight.locked && !isAuthenticated && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(ACCENT, 0.25)}` }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <LockOutlinedIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      That's the free pass. The full write-up and follow-up questions need an account.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => requireSignin()}
                    sx={{
                      backgroundColor: ACCENT,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: ACCENT, filter: "brightness(0.95)" },
                    }}
                  >
                    Sign in for the full analysis
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {!isAuthenticated && (
            <Typography variant="caption" sx={{ display: "block", mb: 3, color: theme.palette.text.secondary }}>
              This log is public — anyone with the link can open it. Sign in to claim it and make it private.
            </Typography>
          )}

          {analyzing ? (
            <ParseProgress log={log} />
          ) : (
            <>
            <AnalysisTabs tabs={PUBLIC_TABS} value={tab} onChange={selectTab} analysis={log.analysis} />

            {tab === "overview" && (
              <>
                {gated ? (
                  <BlurredRank log={log} theme={theme} onSignin={() => requireSignin()} />
                ) : (
                  <ScoreHeader log={log} />
                )}
                <LogSummary log={log} />
                <Box sx={{ mt: 1.5 }}>
                  <HardwareId log={log} />
                </Box>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
                  FLIGHT TIMELINE
                </Typography>
                <LogTimeline log={log} />
              </>
            )}

            {/* No RCA export on demo logs — the numbers are invented, so a report
                built from them would read as a real record. */}
            {tab === "findings" && <LogFindings log={log} showReport={!log.demo} paramAudit={paramAudit} />}

            {gated ? (
              tab !== "overview" &&
              tab !== "findings" && (
                <SignInCard
                  theme={theme}
                  title="Deep analysis needs an account"
                  body="The overview and findings above are the free pass. Filters, oscillation, PID, compass, hardware, parameters and charts come with an account."
                  onSignin={() => requireSignin()}
                />
              )
            ) : (
              <>
                {DEEP_PANELS[tab] && (
                  <PublicAnalysisPanel log={log} module={tab} render={DEEP_PANELS[tab]} />
                )}

                {/* Demo logs carry no parameters to audit. */}
                {tab === "params" &&
                  (log.demo ? (
                    <PanelUnavailable reason="Demo logs carry no parameters to audit." />
                  ) : (
                    <LogParamAudit log={log} fetchMessages={fetchMessages} onAudit={setParamAudit} />
                  ))}

                {tab === "charts" && <LogCharts log={log} fetchMessages={fetchMessages} />}
              </>
            )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PublicLogDetail;
